// R24 — WHERE IT LANDED, AND WHY THE TWO SENTENCES THAT SAY SO CAME OUT TOGETHER.
//
// A generated reading goes to a room's shelf. These are the only two sentences on this site that
// TELL her which one — one after it arrives, one while it is still coming through — and both name
// the room by calling `roomLabel(room)`. That is a crossing: the SERVER decides the room (from the
// saved type, `ROOM_FOR_TYPE`), and this is the client telling her about a decision it did not make.
// If the room named here is not the room it actually landed in, she opens the shelves drawer, picks
// the shelf she was just told to pick, and finds nothing on it. Nothing throws and nothing logs —
// the same silence R17 found on the other end of this same value.
//
// Hookless, so `scripts/r24-shelf-destination.mjs` CALLS them and reads the sentence back, and holds
// every room named in App.tsx's own copies of this line against the real roster (R20's lesson: a
// literal at a call site is held by nothing at all unless something goes and finds it).
//
// ---------------------------------------------------------------------------------------------
// 14-B6a — **AND THE THING R24 EXTRACTED WAS BEING CALLED AT ONE SURFACE IN NINETEEN.**
//
// R24 took these two sentences out "so a gate CALLS them", and eighteen surfaces kept a hand-typed
// copy of the WHOLE block around them: the shelf line, Save to My Room in its three states, Download
// PDF. Only the numerology room called this file. R24's fixture parsed the eighteen ROOM NAMES out of
// App.tsx and held those against the roster — which is the half that was wrong at the time — so
// nothing anywhere could see that the CONTROLS were eighteen copies. That is W12/R26/R38's shape
// exactly: the capability lives on the surface that needed it first, and every later surface copies
// the code instead of the component. A Spanish delivery block would have reached one room in nineteen.
//
// **AND THE COPIES HAD ALREADY DRIFTED FROM THIS FILE, WITH A CONSEQUENCE A MEMBER PAYS FOR.**
// The button below is disabled when `saved || saving`; fourteen of the copies disabled on `saving`
// alone, so a second tap POSTed the same reading a second time and put two of it on her shelf, under
// a button already reading "✓ Saved to My Room". The law is here; the drift is gone.
//
// `language` is REQUIRED and deliberately not optional-with-a-default (R27's live defect was a
// capability behind an optional prop that neither call site passed). Required, the TYPE refuses a
// call site that forgets it, which is the totality argument R42 used on `PAGE_TITLES` — and this
// house runs `npm run typecheck`, so it is an instrument rather than a hope.
//
// The room NAME inside the sentence stays English on purpose: `roomLabel` resolves what
// `src/catalogs/rooms.tsx` publishes, and that roster is declared `brand.` ENGLISH BY DESIGN
// (14-B5). "En tu estante de Astrology" is 14-B3's seam one room over, and it is the deliberate one.
import { Check } from "lucide-react";
import type { ReactNode } from "react";
import { roomLabel } from "../rooms.js";
import { t } from "../strings.js";

export function ResultDestinations({ room, language, saved, saving, onSaveMyRoom, onPdf, extras }: {
  room: string;
  language: string;
  saved: boolean;
  saving?: boolean;
  onSaveMyRoom: () => void;
  // Absent for a reading (her ruling, 2026-10-17): readings are opened in the house, never downloaded.
  onPdf?: () => void;
  extras?: ReactNode;
}) {
  return (
    <div className="report-actions result-destinations">
      <span className="result-dest__shelf"><Check size={15} /> {t(language, "result.onShelf", { room: roomLabel(room) })}</span>
      <button type="button" className="button button--ghost" onClick={onSaveMyRoom} disabled={saved || saving}>
        {saved ? t(language, "result.saved") : saving ? t(language, "result.saving") : t(language, "result.save")}
      </button>
      {onPdf ? <button type="button" className="button button--ghost" onClick={onPdf}>{t(language, "reports.download")}</button> : null}
      {extras}
    </div>
  );
}

// SH1 — the no-hostage reassurance, shown while a reading is coming through. Warm, plain, no
// machinery, no em dashes: leaving never loses the work; it finishes and waits on the shelf.
//
// 14-B6a measured this one too and did NOT widen it: it renders in the numerology room and nowhere
// else, though nineteen surfaces generate. Putting a sentence a member has never seen onto eighteen
// more screens is a PRODUCT change, not a translation, so it is flagged for her word in RUNBOOK.md
// rather than taken inside a Spanish round.
export function NoHostageNote({ room, language }: { room: string; language: string }) {
  return (
    <p className="no-hostage-note" role="status">
      {t(language, "result.noHostage", { room: roomLabel(room) })}
    </p>
  );
}
